/**
 * ViewSettingsPopover – Eye icon popover for adjusting view settings.
 * 5 sections: Navigation, Header, Sidebar, Tasks, Toolbar.
 */

import { useRef, useEffect, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import { mainNavStore, NAV_STAGES_ORDERED, type NavStage } from '../../../stores/mainNavStore';
import { toolbarStore, TOOLBAR_STAGES_ORDERED, type ToolbarStage } from './toolbarStore';
import {
  useHeaderControls,
  useSidebarControls,
  useTaskDetailControls,
  useIsSidebarCollapsed,
  useVisibleHeaderRows,
  useHeaderMode,
} from '../BoardLayoutContext';
import { DETAIL_LEVELS, type TaskDetailLevel } from '../../hooks/useBoardTaskDetailLevel';
import type { HeaderRowId } from '../../hooks/useBoardHeaderResize';
import {
  popoverStyle,
  sectionLabelStyle,
  sectionDividerStyle,
} from './toolbarStyles';

// ── Button labels ──

const NAV_LABELS: Record<NavStage, string> = {
  tiny: 'T',
  small: 'S',
  medium: 'M',
  icons: 'I',
  expanded: 'E',
};

const TOOLBAR_LABELS: Record<ToolbarStage, string> = {
  tiny: 'T',
  small: 'S',
  medium: 'M',
  full: 'F',
};

const DETAIL_LABELS: Record<TaskDetailLevel, string> = {
  minimal: 'Min',
  kompakt: 'Komp',
  normal: 'Norm',
  detailliert: 'Det',
};

const HEADER_ROW_LABELS: Record<HeaderRowId, string> = {
  jahre: 'J',
  monate: 'M',
  kw: 'KW',
  tage: 'T',
};

// ── Styles ──

const activeBtnStyle: React.CSSProperties = {
  backgroundColor: '#3b82f6',
  color: '#fff',
  borderColor: '#3b82f6',
};

const inactiveBtnStyle: React.CSSProperties = {
  backgroundColor: '#f3f4f6',
  color: '#374151',
  borderColor: '#d1d5db',
};

const segBtnBase: React.CSSProperties = {
  height: 26,
  padding: '0 8px',
  fontSize: 11,
  fontWeight: 600,
  border: '1px solid #d1d5db',
  borderRadius: 5,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 28,
  transition: 'background-color 0.1s, color 0.1s',
};

export function ViewSettingsPopover({
  onClose,
  triggerRef,
}: {
  onClose: () => void;
  triggerRef?: RefObject<HTMLElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Click-outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (triggerRef?.current?.contains(e.target as Node)) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef]);

  // ── Store values ──
  const navStage = useSelector(mainNavStore, s => s.context.stage);
  const tbStage = useSelector(toolbarStore, s => s.context.stage);

  // ── Context values ──
  const headerControls = useHeaderControls();
  const sidebarControls = useSidebarControls();
  const taskDetailControls = useTaskDetailControls();
  const isCollapsed = useIsSidebarCollapsed();
  const visibleRows = useVisibleHeaderRows();
  const headerMode = useHeaderMode();

  return (
    <div ref={ref} style={popoverStyle} onClick={e => e.stopPropagation()}>
      {/* ── NAVIGATION ── */}
      <div style={sectionLabelStyle}>{t('boardv3.viewSettings.navigation')}</div>
      <div style={{ display: 'flex', gap: 4 }}>
        {NAV_STAGES_ORDERED.map(stage => (
          <button
            key={stage}
            style={{ ...segBtnBase, ...(navStage === stage ? activeBtnStyle : inactiveBtnStyle) }}
            onClick={() => mainNavStore.send({ type: 'nav.setStage', stage })}
            title={stage}
          >
            {NAV_LABELS[stage]}
          </button>
        ))}
      </div>

      {/* ── HEADER ── */}
      <div style={sectionDividerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={sectionLabelStyle}>{t('boardv3.viewSettings.header')}</span>
          {headerMode === 'manual' && (
            <button
              style={{
                fontSize: 9,
                color: '#6b7280',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
              onClick={() => headerControls.setAutoMode()}
            >
              Auto
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['jahre', 'monate', 'kw', 'tage'] as HeaderRowId[]).map(row => {
            const isVisible = visibleRows.includes(row);
            const isKw = row === 'kw';
            return (
              <button
                key={row}
                style={{
                  ...segBtnBase,
                  ...(isVisible ? activeBtnStyle : inactiveBtnStyle),
                  ...(isKw ? { opacity: 0.7, cursor: 'default' } : {}),
                }}
                onClick={isKw ? undefined : () => headerControls.toggleRow(row as 'jahre' | 'monate' | 'tage')}
                disabled={isKw}
                title={isKw ? t('boardv3.viewSettings.cwAlwaysVisible') : `${row} ${isVisible ? t('boardv3.viewSettings.hide') : t('boardv3.viewSettings.show')}`}
              >
                {HEADER_ROW_LABELS[row]}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── SIDEBAR ── */}
      <div style={sectionDividerStyle}>
        <div style={sectionLabelStyle}>{t('boardv3.viewSettings.sidebar')}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            style={{ ...segBtnBase, ...(isCollapsed ? activeBtnStyle : inactiveBtnStyle) }}
            onClick={() => { if (!isCollapsed) sidebarControls.toggleCollapse(); }}
          >
            {t('boardv3.viewSettings.on')}
          </button>
          <button
            style={{ ...segBtnBase, ...(!isCollapsed ? activeBtnStyle : inactiveBtnStyle) }}
            onClick={() => { if (isCollapsed) sidebarControls.toggleCollapse(); }}
          >
            {t('boardv3.viewSettings.off')}
          </button>
        </div>
      </div>

      {/* ── TASKS ── */}
      <div style={sectionDividerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={sectionLabelStyle}>{t('boardv3.viewSettings.tasks')}</span>
          {taskDetailControls.mode === 'manual' && (
            <button
              style={{
                fontSize: 9,
                color: '#6b7280',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
              onClick={() => taskDetailControls.setAutoMode()}
            >
              Auto
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {DETAIL_LEVELS.map(level => (
            <button
              key={level}
              style={{ ...segBtnBase, ...(taskDetailControls.detailLevel === level ? activeBtnStyle : inactiveBtnStyle) }}
              onClick={() => taskDetailControls.setLevel(level)}
              title={level}
            >
              {DETAIL_LABELS[level]}
            </button>
          ))}
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div style={sectionDividerStyle}>
        <div style={sectionLabelStyle}>{t('boardv3.viewSettings.toolbar')}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {TOOLBAR_STAGES_ORDERED.map(stage => (
            <button
              key={stage}
              style={{ ...segBtnBase, ...(tbStage === stage ? activeBtnStyle : inactiveBtnStyle) }}
              onClick={() => toolbarStore.send({ type: 'toolbar.setStage', stage })}
              title={stage}
            >
              {TOOLBAR_LABELS[stage]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
